import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth, apiGet } from "@/contexts/AuthContext";
import { CheckCircle, Loader } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EnrollmentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }

    const poll = async () => {
      try {
        const { data } = await apiGet(`${API}/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refreshUser();
        } else if (data.payment_status === "expired" || data.status === "expired") {
          setStatus("expired");
        } else if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus("timeout");
        }
      } catch {
        if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus("error");
        }
      }
    };
    poll();
  }, [sessionId, attempts]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#F7F5F0' }}>
      <div className="w-full max-w-md text-center">
        {status === "checking" && (
          <div className="earthy-card" data-testid="payment-checking">
            <Loader size={32} strokeWidth={1.5} className="mx-auto mb-4 animate-spin" style={{ color: '#4A5D4E' }} />
            <h1 className="text-2xl mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>Confirming your payment...</h1>
            <p className="text-sm" style={{ color: '#5B665E' }}>This should only take a moment.</p>
          </div>
        )}
        {status === "paid" && (
          <div className="earthy-card" data-testid="payment-success">
            <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: 'rgba(74,93,78,0.1)' }}>
              <CheckCircle size={32} strokeWidth={1.5} style={{ color: '#4A5D4E' }} />
            </div>
            <h1 className="text-3xl mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>You're In!</h1>
            <p className="text-base mb-8" style={{ color: '#5B665E' }}>
              Welcome to Causes and Conditions. Your 4-week journey starts now.
            </p>
            <Link to="/dashboard" className="earthy-btn earthy-btn-primary text-base inline-block" data-testid="go-dashboard-btn">
              Go to Your Dashboard
            </Link>
          </div>
        )}
        {(status === "expired" || status === "error" || status === "timeout") && (
          <div className="earthy-card" data-testid="payment-failed">
            <h1 className="text-2xl mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>
              {status === "expired" ? "Payment session expired" : "Something went wrong"}
            </h1>
            <p className="text-sm mb-6" style={{ color: '#5B665E' }}>
              {status === "expired" ? "Your checkout session has expired. Please try again." : "We couldn't confirm your payment. Please try again or contact support."}
            </p>
            <Link to="/register" className="earthy-btn earthy-btn-primary text-base inline-block" data-testid="retry-payment-btn">
              Try Again
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
